import { z } from "zod";

export function onlyDigits(value: string) {
  return value.replace(/\D/g, "");
}

export function isValidCpf(value: string) {
  const cpf = onlyDigits(value);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const calcDigit = (base: string, factor: number) => {
    const total = base.split("").reduce((sum, digit) => sum + Number(digit) * factor--, 0);
    const rest = (total * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return (
    calcDigit(cpf.slice(0, 9), 10) === Number(cpf[9]) &&
    calcDigit(cpf.slice(0, 10), 11) === Number(cpf[10])
  );
}

const requiredText = (field: string, min = 2) =>
  z
    .string({ required_error: `Informe ${field}.` })
    .trim()
    .min(min, `Informe ${field}.`);

export const emailSchema = z
  .string({ required_error: "Informe seu e-mail." })
  .trim()
  .toLowerCase()
  .email("Informe um e-mail valido.");

export const phoneSchema = z
  .string({ required_error: "Informe seu telefone." })
  .transform(onlyDigits)
  .refine((v) => v.length >= 10 && v.length <= 11, "Informe um telefone valido.");

export const cpfSchema = z
  .string({ required_error: "Informe seu CPF." })
  .transform(onlyDigits)
  .refine(isValidCpf, "Informe um CPF valido.");

export const postalCodeSchema = z
  .string({ required_error: "Informe seu CEP." })
  .transform(onlyDigits)
  .refine((v) => v.length === 8, "Informe um CEP valido.");

export const addressSchema = z.object({
  postalCode: postalCodeSchema,
  street: requiredText("a rua"),
  number: requiredText("o numero", 1),
  complement: z.string().trim().optional().default(""),
  neighborhood: requiredText("o bairro"),
  city: requiredText("a cidade"),
  state: z
    .string({ required_error: "Informe o estado." })
    .trim()
    .length(2, "Informe a UF com 2 letras.")
    .transform((v) => v.toUpperCase()),
  country: z.literal("BR").default("BR")
});

export const checkoutItemSchema = z.object({
  shopifyProductGid: z.string().startsWith("gid://shopify/Product/", "Produto Shopify invalido."),
  shopifyVariantGid: z.string().startsWith("gid://shopify/ProductVariant/", "Variante Shopify invalida."),
  sku: z.string().optional(),
  title: z.string().min(1, "Item sem titulo."),
  variantTitle: z.string().optional(),
  imageUrl: z.string().url("URL de imagem invalida.").optional(),
  quantity: z.number().int("Quantidade invalida.").min(1, "Quantidade minima: 1.").max(99, "Quantidade maxima: 99."),
  unitPrice: z.number().int().nonnegative("Preco invalido."),
  discountAmount: z.number().int().nonnegative("Desconto invalido.").default(0),
  finalUnitPrice: z.number().int().nonnegative("Preco final invalido.")
});

export const checkoutSchema = z.object({
  email: emailSchema,
  phone: phoneSchema,
  cpf: cpfSchema,
  firstName: requiredText("seu nome"),
  lastName: requiredText("seu sobrenome"),
  address: addressSchema,
  selectedShippingMethod: z.string().min(1, "Escolha uma opcao de entrega."),
  items: z.array(checkoutItemSchema).min(1, "Seu carrinho esta vazio.")
});

export const shippingRequestSchema = z.object({
  postalCode: postalCodeSchema,
  address: addressSchema.partial().optional(),
  items: z.array(checkoutItemSchema).min(1, "Seu carrinho esta vazio.")
});

export const pixPaymentRequestSchema = z.object({
  checkoutSessionId: z.string().min(8, "Checkout invalido.")
});
